import axios from "axios";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { AssignOrderSchema } from "../../../ValidationSchema";
import ReactSelect from "../../../Components/PortalSelect";

const colourStyles = {
  control: (styles) => ({
    ...styles,
    border: "1px solid #bfc9d4",
    boxShadow: "#000 !important",
    borderRadius: "6px",
    padding: "4px",
    "&:hover": {
      border: "1px solid #bfc9d4",
    },
  }),
  multiValue: (styles) => {
    return {
      ...styles,
      backgroundColor: "#606060",
    };
  },
  multiValueLabel: (styles) => ({
    ...styles,
    color: "#fff",
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: "#000",
    "& > svg": {
      backgroundColor: "#fff",
      borderRadius: "10px",
      marginLeft: "2px",
      marginRight: "2px",
    },
    ":hover": {
      backgroundColor: data.color,
      color: data.color,
    },
  }),
  option: (styles, { data }) => {
    return {
      ...styles,
      backgroundColor: "white",
      color: "#000",
      ":hover": {
        backgroundColor: "#f5f5f5",
      },
    };
  },
};

const AssignCodeSupplierAdd = () => {
  const [orderData, setOrderData] = useState([]);
  // const [assignedOrders, setAssignedOrders] = useState([]);
  const [supplierData, setSupplierData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredProduct, setFilteredProduct] = useState([]);
  const [bindSuppliers, setBindSuppliers] = useState([]);
  const location = useLocation();
  const productIdFromState = location?.state?.product_id;
  const orderIdFromState = location?.state?.orderId;
  const supplierIds = location?.state?.supplierIds;
  const route = location?.state?.route;
  const buyerUserIdFromState = location?.state?.buyerUserId;

  const Navigate = useNavigate();

  const [initialValues, setInitialValues] = useState({
    order_id: "",
    supplier_id: [],
    remark: "",
    status: "Pending",
  });

  useEffect(() => {
    if (productIdFromState) {
      setInitialValues((prev) => ({
        ...prev,
        order_id: productIdFromState,
        status: "Pending",
      }));
    }
  }, [productIdFromState]);

  const SupplierData = () => {
    axios
      .get(`${process.env.REACT_APP_URL}/admin/offline_supplier`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("Token")}`,
        },
      })
      .then((res) => {
        let users = res?.data?.data?.users || [];

        // ✅ Step 1: only active suppliers
        const activeSuppliers = users.filter((u) => u.status === "active");

        // optional: remove duplicates
        const uniqueSupplierIds = [...new Set(supplierIds)];

        // The person placing THIS order can themselves be a supplier
        // account elsewhere in the system (e.g. an offline_supplier who
        // also orders raw materials) — being an active supplier, their own
        // account showed up as something that could be "assigned" to
        // fulfil their own order. It's the order's buyer we need to
        // exclude here, not whoever happens to be logged in doing the
        // assigning (that was the earlier, wrong fix — an admin assigning
        // a supplier on someone else's behalf isn't the buyer at all, so
        // excluding the logged-in user didn't touch this case).
        const buyerUserId = buyerUserIdFromState ?? null;

        // ✅ Step 3: filter out those active suppliers who already exist in supplierIds
        const availableSuppliers = activeSuppliers.filter(
          (s) =>
            !uniqueSupplierIds.includes(s.user_id) &&
            s.user_id !== buyerUserId
        );


        // ✅ Step 4: bind dropdown options
        const suppliers = availableSuppliers.map((item) => ({
          label: item?.name || "Untitled",
          value: item?.user_id,
        }));

        setSupplierData(suppliers);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err.response?.data?.message === "Unauthorized!") {
          Navigate("/");
        } else {
          toast.error("Failed to fetch supplier data");
        }
      });
  };

  const AddRole = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: AssignOrderSchema,

    onSubmit: (values, action) => {
      const payload = {
        ...values,
        supplier_ids: values.supplier_id,
        order_id: orderIdFromState,
      };

      axios
        .post(
          `${process.env.REACT_APP_URL}/admin/quotation/send/supplier/${productIdFromState}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
            },
          }
        )
        .then((res) => {
          if (res.status === 201) {
            toast.success(`${res?.data?.message}`);
            Navigate(route, { state: res.data.message });
          }
        })
        .catch((err) => {
          // The API returns `message`, not `msg` — this always showed
          // "undefined" instead of the real reason (e.g. a supplier already
          // approved for this order, or already assigned to this one).
          toast.error(
            err?.response?.data?.message || "Failed to assign supplier"
          );
        });
    },
  });

  useEffect(() => {
    SupplierData();
    // AssignedOrders();
  }, [productIdFromState]);

  if (loading) {
    return (
      <div className="w-100">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="al">
      <Toaster position="top-center" reverseOrder={false} />

      <div className="al-head">
        <div>
          <h1 className="al-title">Assign Supplier</h1>
          <p className="al-sub">Assign supplier(s) to this product.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn" onClick={() => Navigate(-1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
        </div>
      </div>

      <div className="al-card">
        <div className="al-cardhead">
          <div className="al-cardhead__icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg></div>
          <h3 className="al-cardhead__title">Assignment Details</h3>
        </div>
            <div className="al-form">
                <form onSubmit={AddRole.handleSubmit} className="al-form__full">
                  {/* {!productIdFromState && (
                    <div className="form-group">
                      <label style={{ fontSize: "14px", color: "black" }}>
                        Order
                      </label>
                      <ReactSelect
                        options={orderData}
                        isClearable={false}
                        styles={colourStyles}
                        components={{
                          DropdownIndicator: () => null,
                          IndicatorSeparator: () => null,
                        }}
                        placeholder="Select Order"
                        name="order_id"
                        onChange={(e) =>
                          AddRole?.setFieldValue("order_id", e?.value)
                        }
                        value={
                          orderData.find(
                            (item) => item.value === AddRole.values.order_id
                          ) || null
                        }
                      />

                      {AddRole?.errors?.order_id &&
                        AddRole?.touched?.order_id && (
                          <h6 className="text-danger mt-2 ml-1">
                            {AddRole?.errors?.order_id}
                          </h6>
                        )}
                    </div>
                  )} */}

                  <div className="form-group">
                    <label className="al-label">
                      Supplier
                    </label>
                    <ReactSelect
                      isMulti
                      options={supplierData}
                      isClearable={false}
                      styles={colourStyles}
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Supplier"
                      name="supplier_id"
                      onChange={(selected) => {
                        const values = selected
                          ? selected.map((option) => option.value)
                          : [];
                        AddRole?.setFieldValue("supplier_id", values);
                      }}
                      value={AddRole?.values?.supplier_id
                        ?.map((id) => {
                          const option = supplierData.find(
                            (item) => item.value === id
                          );
                          return option || null;
                        })
                        .filter(Boolean)}
                    />

                    {AddRole?.errors?.supplier_id &&
                      AddRole?.touched?.supplier_id && (
                        <h6 className="text-danger mt-2 ml-1">
                          {AddRole?.errors?.supplier_id}
                        </h6>
                      )}
                  </div>

                  {/* {!productIdFromState && (
                    <div className="form-group">
                      <label style={{ fontSize: "14px", color: "black" }}>
                        Order Status
                      </label>
                      <ReactSelect
                        options={[
                          { label: "Pending", value: "Pending" },
                          { label: "Completed", value: "Completed" },
                        ]}
                        isClearable={false}
                        styles={colourStyles}
                        components={{
                          DropdownIndicator: () => null,
                          IndicatorSeparator: () => null,
                        }}
                        placeholder="Select Status"
                        name="status"
                        onChange={(e) =>
                          AddRole?.setFieldValue("status", e?.value)
                        }
                      />
                      {AddRole?.errors?.status && AddRole?.touched?.status && (
                        <h6 className="text-danger mt-2 ml-1">
                          {AddRole?.errors?.status}
                        </h6>
                      )}
                    </div>
                  )} */}

                  {/* <div className="form-group">
                    <label style={{ fontSize: "14px", color: "black" }}>
                      Remark
                    </label>
                    <textarea
                      className="form-control"
                      placeholder="Enter remark"
                      name="remark"
                      value={AddRole.values.remark}
                      onChange={AddRole.handleChange}
                      onBlur={AddRole.handleBlur}
                    />
                    {AddRole?.errors?.remark && AddRole?.touched?.remark && (
                      <h6 className="text-danger mt-2 ml-1">
                        {AddRole?.errors?.remark}
                      </h6>
                    )}
                  </div> */}

                  <div className="al-actionbar">
                    <button type="button" className="al-btn" onClick={() => Navigate(-1)}>Cancel</button>
                    <button type="submit" className="al-btn al-btn--primary">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                      Assign Supplier
                    </button>
                  </div>
                </form>
            </div>
        </div>
    </div>
  );
};

export default AssignCodeSupplierAdd;
